import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Image} from 'primereact/image'
import {InputText} from 'primereact/inputtext'
import {useEffect, useMemo, useRef} from 'react'
import {useHistory} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import FlexBox from '../layout/FlexBox'
import {
    getPathwayEventKey,
    onPathwayTableChangeRemoveListener,
    pathwayEvent,
    pathwayEventTypes,
} from '../styled-component/pathway/broadcast/PathwayEvent'
import {
    getPatientEventKey,
    onPatientPathwayChangeRemoveListener,
    patientEvent,
    patientEventTypes,
} from '../styled-component/patient/broastcast/PatientEvent'
import {useListPathways} from './query-keys/PathwayKeysManager'

const PatientByPathwayTable = () => {
    useSignals()
    //#region Import
    const history = useHistory()
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const queryClient = useQueryClient(0)
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const filterValue = useSignal('')
    //#endregion

    //#region Ref
    const blockScrollRef = useRef()
    //#endregion

    //#region Memo
    const InitialFilterRequest = useMemo(
        () => ({
            hcpId: userRole === SystemRole.USER_WITH_ROLE ? user?.id : null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }),
        [currentHospital?.id, user?.id, userRole],
    )
    //#endregion

    //#region Query
    const {data: pathwayOptions} = useListPathways(
        user?.id,
        true,
        InitialFilterRequest,
        !!user?.id && (userRole === SystemRole.ADMIN || !!currentHospital?.id),
    )

    //#endregion

    //#region Handle Function
    const handleMouseEnter = () => {
        blockOverflownScroll()
    }

    const handleMouseLeave = () => {
        unblockOverflownScroll()
    }
    //#endregion

    //#region Event Lister for Toggle
    useEffect(() => {
        const {pathwayKeys} = QueryKeyManager(QueryKeyTypes.pathway, user?.id)

        // TABLE CHANGE
        pathwayEvent.on(
            `${getPathwayEventKey(pathwayEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                queryClient.invalidateQueries({
                    queryKey: pathwayKeys.pattern(),
                })
            },
        )

        // PATIENT PATHWAY CHANGE
        patientEvent.on(
            `${getPatientEventKey(patientEventTypes.PATHWAY_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                queryClient.invalidateQueries({
                    queryKey: pathwayKeys.pattern(),
                })
            },
        )

        return () => {
            onPatientPathwayChangeRemoveListener()
            onPathwayTableChangeRemoveListener()
        }
    }, [])
    //#endregion

    return (
        <div className="h-full w-full border-1 border-gray-200 border-round-lg overflow-y-auto">
            <div className="sticky w-full bg-white p-2 z-1">
                <div className="flex flex-column gap-3 justify-content-center align-items-center text-center col-12 bg-gray-50 border-round-top-lg">
                    <h4 className="m-0 p-0 font-bold overflow-dot overflow-text-1">
                        {translateCommons('Patients by Pathway')}
                    </h4>
                    <p className="text-sm pb-2">
                        {translateCommons(
                            'Patients organized and categorized based on their care pathways for efficient healthcare management.',
                        )}
                    </p>
                </div>
                <hr className="m-0" />
                <div className="col-12 px-0">
                    <span className="p-input-icon-right w-full">
                        <i className="pi pi-search" />
                        <InputText
                            className="h-3rem w-full"
                            placeholder={translateCommons('Search by name')}
                            value={filterValue.value || ''}
                            onChange={e => (filterValue.value = e.target.value)}
                        />
                    </span>
                </div>
            </div>
            <div
                ref={blockScrollRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}>
                <ul className="p-2 m-0">
                    {pathwayOptions && pathwayOptions.length > 0 ? (
                        pathwayOptions.map(
                            item =>
                                item?.label
                                    ?.toLowerCase()
                                    .includes(
                                        filterValue.value?.toLowerCase(),
                                    ) && (
                                    <li
                                        key={item.value}
                                        className="flex align-items-center border-bottom-1 border-gray-100 hover:bg-gray-50 cursor-pointer"
                                        onClick={() =>
                                            history.push(
                                                `patients?pathwayId=${item.value}`,
                                            )
                                        }>
                                        <div className="col-12">
                                            <FlexBox
                                                className={
                                                    'justify-content-between'
                                                }>
                                                <div>
                                                    {item ? (
                                                        <p className="text-sm overflow-dot overflow-text-1 font-semibold">
                                                            {`${item.label} (${
                                                                item.extraData
                                                                    ? item.extraData
                                                                    : '0'
                                                            } ${translateCommons(
                                                                'results',
                                                            )})`}
                                                        </p>
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                                <div>
                                                    <Image
                                                        height="40"
                                                        src={`${item.data}`}
                                                    />
                                                </div>
                                            </FlexBox>
                                        </div>
                                    </li>
                                ),
                        )
                    ) : (
                        <li>
                            <div className="col-12">
                                <p className="text-center text-sm font-semibold text-600">
                                    {translateCommons(
                                        'No pathway has been set up',
                                    )}
                                </p>
                            </div>
                        </li>
                    )}
                </ul>
            </div>
        </div>
    )
}

export default PatientByPathwayTable
